import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { canvasAtMost } from '../../../theme/canvas';

import { track } from '../../../constants/datadog';
import { EVENT } from '../../../constants/events';
import { userInitials } from '../../../utils/user';

import { PATHS } from '../../../routes/paths';

import { colorByKey } from '../../../theme/utils';

import auth from '../../../Auth/Auth';
import AvatarUser from '../../AvatarUser';
import ButtonBase from '../../ButtonBase';
import ListItemIcon from '../../ListItemIcon';
import { LaptopLUp } from '../../MediaQuery';
import { useMenu } from '../../Menu';
import Typography from '../../Typography';
import { IntegrationIcon } from '../../icons';
import SvgCog from '../../icons/Cog';
import { useSidebar } from '../../../context/SidebarProvider';
import React from 'react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components-ui/DropdownMenu';
import { LogOut, SquareArrowOutUpRight } from 'lucide-react';
import useUserStore from '../../../store/user';
import useOrganizationStore from '../../../store/organization';
import { Can } from '../../../Auth/rbac';
import { setHeaderAutoCollapseEnabled } from '../../../utils/headerAutoCollapse';
import { useAutoCollapseEnabled } from '../../../hooks/useAutoCollapseEnabled';
import { PLANS } from '../../../entitlements/plans';
import { setStarterPlanOverride } from '../../../entitlements/starterPreviewOverride';
import { canUsePreviewControls } from '../../../entitlements/previewAudience';
import { useStarterPlanOverride } from '../../../entitlements/useEntitlement';
import { ROLES, type Role } from '../../../Auth/rbac/roles';
import { setRolePreviewOverride } from '../../../Auth/rbac/rolePreviewOverride';
import { useRolePreviewOverride } from '../../../Auth/rbac/useRole';

/** Sentinel for "no override" — Radix radio groups cannot hold a null value. */
const REAL_SEAT = 'real';

const SEAT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: REAL_SEAT, label: 'My real seat' },
  { value: ROLES.ADMIN, label: 'Admin' },
  { value: ROLES.MEMBER, label: 'Member' },
  { value: ROLES.READONLY, label: 'Read-only' },
];

const ButtonBaseStyled = styled(ButtonBase)`
  width: 100%;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  letter-spacing: 0.4px;

  color: ${colorByKey('blueyGrey')};

  margin-top: 16px;
  padding: 12px 8px !important;

  &:hover {
    color: ${colorByKey('charcoalGrey')};
    background: #f4f4f6;
  }
`;

const AvatarUserStyled = styled(AvatarUser)`
  background: #e3e4e8;
  span {
    font-size: 12px;
  }

  ${canvasAtMost(1440)} {
    margin-right: 0;
  }
`;

const UserInfoContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 8px;
  font-size: 14px;
  p {
    color: #666;
    font-weight: 400;
    margin-top: 4px;
    line-height: 1.2;
  }
`;

const UserInfoStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledTypography = styled(Typography)`
  color: #b0b1bd;
  font-weight: 400;
  font-size: 12px;
  max-width: 130px;
`;

function Account() {
  const { anchorEl, toggleMenu } = useMenu();
  const { isCollapsed } = useSidebar();
  const { getHelpPageUrl } = useOrganizationStore();
  const { user, isSupportUser } = useUserStore();
  const autoCollapseEnabled = useAutoCollapseEnabled();
  const planPreview = useStarterPlanOverride();
  const rolePreview = useRolePreviewOverride();

  function handleLogOut() {
    // TODO: Auth should be used through react hook.
    auth.logout();
  }

  function handleAutoCollapseToggle(checked: boolean) {
    setHeaderAutoCollapseEnabled(checked);
    window.dispatchEvent(new CustomEvent('headerAutoCollapseChanged'));
  }

  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <ButtonBaseStyled ref={anchorEl} onClick={toggleMenu} data-cy="accountTopBarBtn">
          <AvatarUserStyled
            style={{ marginRight: isCollapsed ? 0 : 8 }}
            size={32}
            email={user.email}
            fallback={userInitials(user)}
            data-cy="userAvatarIcon"
          />
          <LaptopLUp>
            <UserInfoStyled>
              <Typography style={{ color: '#41424e', fontSize: 13 }}>
                {user.firstname} {user.lastname}
              </Typography>
              <StyledTypography>{user.email}</StyledTypography>
            </UserInfoStyled>
          </LaptopLUp>
        </ButtonBaseStyled>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="min-w-56 rounded-lg border border-[#E4E4E7] bg-white text-neutral-600 shadow-lg"
        side="right"
        align="end"
        sideOffset={-4}
        alignOffset={32}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="gap-2 text-left text-sm">
            <UserInfoContainerStyled>
              <p>
                {user.firstname} {user.lastname}
              </p>
              <p style={{ color: '#b0b1bd', fontWeight: 400 }}>{user.email}</p>
            </UserInfoContainerStyled>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-neutral-100" />
        <DropdownMenuGroup>
          {/* Integrations links to /settings?tab=integrations — same route gate
              as Settings, so we use the same permission. Hiding for Read-Only
              avoids a dead-end click into the route-guarded 404. */}
          <Can permission="team.settings.view">
            <Link
              to={PATHS.APP.SETTINGS_INTEGRATIONS}
              onClick={() => {
                track(EVENT.ROUTE_SETTINGS_INTEGRATIONS);
                toggleMenu();
              }}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <DropdownMenuItem className="focus:!bg-seasalt">
                <ListItemIcon>
                  <IntegrationIcon />
                </ListItemIcon>
                Integrations
              </DropdownMenuItem>
            </Link>
            <Link
              to={PATHS.APP.SETTINGS}
              onClick={() => {
                track(EVENT.ROUTE_SETTINGS);
                toggleMenu();
              }}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <DropdownMenuItem className="focus:!bg-seasalt">
                <ListItemIcon>
                  <SvgCog />
                </ListItemIcon>
                Settings
              </DropdownMenuItem>
            </Link>
          </Can>
        </DropdownMenuGroup>
        {isSupportUser ? (
          <>
            <DropdownMenuSeparator className="bg-neutral-100" />
            <DropdownMenuGroup>
              <DropdownMenuCheckboxItem
                checked={autoCollapseEnabled}
                onCheckedChange={handleAutoCollapseToggle}
                className="focus:!bg-seasalt"
                onSelect={(e) => e.preventDefault()}
              >
                Auto-collapse header on scroll
              </DropdownMenuCheckboxItem>
              {/* Reviews the locked Starter surfaces without a Starter team to log into. Narrower
                  than `isSupportUser`: it rewrites the plan for the whole session. */}
              {canUsePreviewControls(user?.email) ? (
                <DropdownMenuCheckboxItem
                  checked={planPreview === PLANS.STARTER}
                  onCheckedChange={(checked) => setStarterPlanOverride(checked ? PLANS.STARTER : null)}
                  className="focus:!bg-seasalt"
                  onSelect={(e) => e.preventDefault()}
                >
                  Preview as Nexoya Starter
                </DropdownMenuCheckboxItem>
              ) : null}
            </DropdownMenuGroup>

            {/* A support account bypasses plan gating, so it is the one seat that can
                never see the Nexoya Starter experience truthfully. Previewing as `agency`
                (the team-admin seat an agency actually gets) is how those surfaces get
                reviewed. Presentation only — the API still enforces the real role. */}
            {canUsePreviewControls(user?.email) ? (
              <>
                <DropdownMenuSeparator className="bg-neutral-100" />
                <DropdownMenuLabel className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-[0.6px] text-neutral-400">
                  Preview as seat
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup
                  value={rolePreview ?? REAL_SEAT}
                  onValueChange={(value) => setRolePreviewOverride(value === REAL_SEAT ? null : (value as Role))}
                >
                  {SEAT_OPTIONS.map(({ value, label }) => (
                    <DropdownMenuRadioItem
                      key={value}
                      value={value}
                      className="focus:!bg-seasalt"
                      onSelect={(e) => e.preventDefault()}
                    >
                      {label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </>
            ) : null}
          </>
        ) : null}
        <DropdownMenuSeparator className="bg-neutral-100" />
        <DropdownMenuGroup>
          <DropdownMenuItem
            onSelect={() => window.open(getHelpPageUrl(), '_blank', 'noopener,noreferrer')}
            className="focus:!bg-seasalt"
          >
            <ListItemIcon>
              <SquareArrowOutUpRight className="h-3.5 w-3.5" />
            </ListItemIcon>
            Help page
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator className="bg-neutral-100" />
        <DropdownMenuItem onSelect={handleLogOut} className="focus:!bg-seasalt">
          <ListItemIcon>
            <LogOut className="h-3.5 w-3.5" />
          </ListItemIcon>
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default Account;
